import optimizelySdk from '@optimizely/optimizely-sdk';
import get from 'lodash/get';
import uniq from 'lodash/uniq';
import isNull from 'lodash/isNull';
import axios from 'axios';
import uuid from 'uuid/v4';
import store from '../store';
import utility from './utility';

// as advised by cro-metrics, temporarily not using cache strategy
const EXPIRATION_PERIOD_IN_DAYS = 0;

const DATAFILE_URL = process.env.OPTIMIZELY_URL || null;

const FEATURE_TO_VARIATION_CONFIG = require('./optimizely-config');

const isDev = () => {
  const env = process.env.NODE_ENV;
  return (env === 'development') || (env === 'stage') || (env === 'preprod');
};

const { LOG_LEVEL } = optimizelySdk.enums;
const optimizelyDebugMode = (store && store.getters && store.getters.optimizelyDebugMode) || false;
optimizelySdk.setLogLevel(optimizelyDebugMode ? LOG_LEVEL.DEBUG : LOG_LEVEL.ERROR);

const getDateDifference = (tsOne, tsTwo) => {
  const diffTime = Math.abs(tsOne - tsTwo);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

const isExpired = lastFetchDate => getDateDifference(Date.now(), lastFetchDate) > EXPIRATION_PERIOD_IN_DAYS;

const getDefaultAttributes = () => {
  const isSubscriber = (store.getters.user && store.getters.user.is_subscribed) || false;
  const isAnonSession = !!(store.getters.anonymousSession && store.getters.userUuid);
  const isDesktopVisitor = !utility.isMobile();
  return {
    isSubscriber,
    isAnonSession,
    isDesktopVisitor,
  };
};

const withDefaults = (obj) => {
  const isEmptyOrNull = typeof obj !== 'object' || Object.keys(obj || {}).length < 1;
  const attributes = isEmptyOrNull ? {} : obj;
  const defaults = getDefaultAttributes();
  return {
    ...defaults,
    ...attributes,
  };
};

const safeDefault = (obj) => {
  const isEmptyOrNull = !obj || !(typeof obj === 'object') || !!(Object.keys(obj || {}).length);
  return isEmptyOrNull ? {} : obj;
};

class Optimizely {
  constructor() {
    this.axios = axios.create();
    this.dataFileUrl = DATAFILE_URL;
    this.dataFile = null;
    this.client = null;
    this.retries = 0;
    this.registeredUuid = null;
    this.initialized = false;
    this.alternateAnonId = null;
    this.loadFeatureAsyncRetries = 0;
    this.currentSessionId = null;
    // Optimizely Instance Management Methods
    this.fetch = this.fetch.bind(this);
    this.isInitialized = this.isInitialized.bind(this);
    this.getClient = this.getClient.bind(this);
    this.createInstance = this.createInstance.bind(this);
    this.getFeaturesFromState = this.getFeaturesFromState.bind(this);
    this.loadFeatureAsync = this.loadFeatureAsync.bind(this);
    this.loadFeatureRecursive = this.loadFeatureRecursive.bind(this);
    this.initializeOptimizely = this.initializeOptimizely.bind(this);
    this.populateFeatureFlags = this.populateFeatureFlags.bind(this);
    this.getEnabledExperiments = this.getEnabledExperiments.bind(this);
    this.getExperimentsForAnalytics = this.getExperimentsForAnalytics.bind(this);
    this.getUserUUID = this.getUserUUID.bind(this);
    this.setAnonSessionState = this.setAnonSessionState.bind(this);
    this.getAnonSessionState = this.getAnonSessionState.bind(this);
    this.preloadEnabledFeatures = this.preloadEnabledFeatures.bind(this);
    this.eventHandler = this.eventHandler.bind(this);
    this.impression = this.impression.bind(this);
    this.getVariable = this.getVariable.bind(this);
    this.preloadRolloutFeatures = this.preloadRolloutFeatures.bind(this);
    this.preloadGlobalConfig = this.preloadGlobalConfig.bind(this);
    this.getMparticleId = this.getMparticleId.bind(this);
    this.getMparticleSessionId = this.getMparticleSessionId.bind(this);
    // Optimizely sdk methods
    this.activate = this.activate.bind(this);
    this.getEnabledFeatures = this.getEnabledFeatures.bind(this);
    this.getFeatureVariableBoolean = this.getFeatureVariableBoolean.bind(this);
    this.getFeatureVariableDouble = this.getFeatureVariableDouble.bind(this);
    this.getFeatureVariableInteger = this.getFeatureVariableInteger.bind(this);
    this.getFeatureVariableString = this.getFeatureVariableString.bind(this);
    this.getForcedVariation = this.getForcedVariation.bind(this);
    this.getVariation = this.getVariation.bind(this);
    this.isFeatureEnabled = this.isFeatureEnabled.bind(this);
    this.setForcedVariation = this.setForcedVariation.bind(this);
    this.track = this.track.bind(this);

    this.initializeOptimizely();
  }

  // BELOW ARE THE METHODS USED TO MANAGE THE DATAFILE & SDK INSTANCE
  fetch() {
    const ctx = this;
    const fetchUrl = ctx.dataFileUrl;
    if (!fetchUrl) {
      throw new Error('No valid datafile url.');
    }
    return this.axios.get(fetchUrl)
      .then(res => res.data)
      .then((dataFile) => {
        ctx.dataFile = dataFile;
        return ctx.dataFile;
      });
  }

  isInitialized() {
    return this.initialized;
  }

  getClient() {
    return this.client;
  }

  createInstance(manualDatafileJson) {
    const { dataFile } = this;
    const options = {};
    if (isDev() && manualDatafileJson) {
      options.datafile = manualDatafileJson;
    } else {
      options.datafile = dataFile;
    }
    this.client = optimizelySdk.createInstance(options);
  }

  getMparticleId() {
    const getCurrentUser = get(window, 'mParticle.Identity.getCurrentUser');
    if (getCurrentUser && getCurrentUser() && getCurrentUser().getMPID && getCurrentUser().getMPID()) {
      return getCurrentUser().getMPID();
    }
    return null;
  }

  getMparticleSessionId() {
    return (window.mParticle && window.mParticle.sessionManager
      && window.mParticle.sessionManager.getSession && window.mParticle.sessionManager.getSession()) || null;
  }

  getUserUUID(getAlternateAnonId) {
    // use fallback id for whole session
    if (this.alternateAnonId) {
      return this.alternateAnonId;
    }
    // if registered id set use it
    if (this.currentSessionId) {
      return this.currentSessionId;
    }
    // if $optimizely init'd with fallback set id and use it for the whole session
    if (getAlternateAnonId) {
      this.alternateAnonId = this.getMparticleId() || this.getMparticleSessionId() || uuid();
      return this.alternateAnonId;
    }
    // otherwise use registered id
    this.registeredUuid = store.getters.userUuid || null;
    this.currentSessionId = this.registeredUuid || this.getMparticleId();
    return this.currentSessionId;
  }

  getFeaturesFromState() {
    const featureFlags = store.getters.featureFlags || null;
    const lastUpdated = store.getters.featureFlagsLastUpdated || null;
    return {
      featureFlags,
      lastUpdated,
    };
  }

  loadFeatureAsync(vuexStoreArg, featureFlag, fallbackValue) {
    return new Promise((resolve, reject) => {
      try {
        this.loadFeatureRecursive(vuexStoreArg, featureFlag, fallbackValue, (err, flagValue) => {
          if (err && flagValue) {
            resolve(flagValue);
          } else if (err && (!flagValue && typeof flagValue !== 'boolean')) {
            throw err;
          }
          resolve(flagValue);
        });
      } catch (err) {
        reject(err);
      }
    });
  }

  loadFeatureRecursive(vuexStoreArg, featureFlag, fallbackValue, cb) {
    const { isInitialized } = this;
    const vuexStore = vuexStoreArg || store;
    const flagValue = vuexStore.getters.featureFlag(featureFlag);
    if (isInitialized() && !isNull(flagValue)) {
      return cb(null, flagValue);
    }
    if (this.loadFeatureAsyncRetries < 100) {
      this.loadFeatureAsyncRetries++;
      return setTimeout(() => {
        this.loadFeatureRecursive(vuexStoreArg, featureFlag, fallbackValue, cb);
      }, 35);
    }
    vuexStore.dispatch('addFallbackFlag', featureFlag);
    const fallbackTo = fallbackValue || false;
    const errMsg = `OPTIMIZELY: loadFeatureAsync failback flag ${featureFlag} to ${fallbackTo}.`;
    return cb(errMsg, fallbackTo);
  }

  setAnonSessionState() {
    const registeredUuid = store.getters.userUuid || null;
    store.dispatch('setAnonymousSession', !registeredUuid);
  }

  getAnonSessionState() {
    return store.getters.anonymousSession;
  }

  initializeOptimizely() {
    const ctx = this;
    const optimizelyDisabled = store.getters.manualDisableOptimizely || null;
    const manualDatafileJson = store.getters.manualDatafileJson || null;

    // ctx.setAnonSessionState();

    if (isDev() && optimizelyDisabled) {
      console.log('Initialization of Optimizely Disabled In Dev Panel.'); // eslint-disable-line no-console
      return;
    }
    if (isDev() && manualDatafileJson) {
      console.log('Optimizely Datafile manually set in Dev Panel.'); // eslint-disable-line no-console
      ctx.createInstance(manualDatafileJson);
      ctx.populateFeatureFlags();
      return;
    }
    ctx.fetch()
      .then(() => {
        ctx.createInstance();
        ctx.populateFeatureFlags();
      });
  }

  populateFeatureFlags(alternateAnonId) {
    const {
      getUserUUID, getFeaturesFromState, preloadEnabledFeatures, preloadRolloutFeatures,
      preloadGlobalConfig, retries,
    } = this;
    const userId = getUserUUID(alternateAnonId);
    const featuresState = getFeaturesFromState();
    const { lastUpdated, featureFlags } = featuresState;

    if (userId === null) {
      this.retries++;
      setTimeout(() => {
        const tryAlternateAnonId = retries > 100;
        this.populateFeatureFlags(tryAlternateAnonId);
      }, 10);
    } else if (!lastUpdated || isExpired(lastUpdated)) {
      const optimizelyEnabledFeaturesArray = preloadEnabledFeatures(userId);
      const optimizelyEnabledFeatures = optimizelyEnabledFeaturesArray.reduce((resultObj, featureKey) => ({
        ...resultObj,
        [featureKey]: true,
      }), {});
      /* eslint-disable */
      const resetExistingFlags = Object.keys(featureFlags).reduce((allToFalse, featureKey) => {
        return {
          ...allToFalse,
          [`${featureKey}`]: false,
        };
      }, {});
      /* eslint-enable */
      const rolloutFeatures = preloadRolloutFeatures();
      const updatedFeatureFlags = {
        ...resetExistingFlags,
        ...optimizelyEnabledFeatures,
        ...rolloutFeatures,
      };
      const globalConfig = preloadGlobalConfig();
      store.dispatch('updateFeatureFlags', updatedFeatureFlags);
      store.dispatch('updateGlobalConfig', globalConfig);
      this.initialized = true;
    }
  }

  preloadEnabledFeatures(userId) {
    const experimentsConfig = FEATURE_TO_VARIATION_CONFIG.experiments;
    return Object.keys(experimentsConfig)
      .map((featureName) => {
        const { experimentKey, enabledWithVariation } = experimentsConfig[featureName];
        const variation = this.getVariation(experimentKey, userId);
        const featureFlag = {
          name: featureName,
          value: variation === enabledWithVariation,
        };
        if (isDev()) {
          // eslint-disable-next-line
          console.log(`PRELOADED OPTIMIZELY FEATURE FLAG '${featureFlag.name}' EVALUATED TO: ${featureFlag.value}`);
        }
        return featureFlag;
      })
      .filter(featureFlag => featureFlag.value)
      .map(featureFlag => featureFlag.name);
  }

  getExperimentsForAnalytics(eventName, userId) {
    const exData = this.getEnabledExperiments(userId);
    return exData;
  }

  getEnabledExperiments(userId) {
    if (!userId) {
      userId = this.getUserUUID();
    }
    if (!this.isInitialized()) {
      return null;
    }
    const experimentsConfig = FEATURE_TO_VARIATION_CONFIG.experiments;
    let experiments = [];
    const variations = Object.keys(experimentsConfig)
      .map((featureName) => {
        const { experimentKey } = experimentsConfig[featureName];
        let variation = null;
        try {
          variation = this.getVariation(experimentKey, userId);
          if (variation) {
            experiments.push(experimentKey);
          }
        } catch (err) {
          console.log(err); // eslint-disable-line no-console
        }
        return variation;
      })
      .filter(variation => variation)
      .join(',');
    experiments = experiments.join(',');
    return {
      variations,
      experiments,
    };
  }

  preloadRolloutFeatures() {
    const { getUserUUID } = this;
    const userId = getUserUUID();
    const rolloutsConfig = FEATURE_TO_VARIATION_CONFIG.rollouts;
    return rolloutsConfig
      .filter(featureName => this.isFeatureEnabled(featureName, userId))
      .reduce((resultObj, featureName) => ({
        ...resultObj,
        [featureName]: true,
      }), {});
  }

  preloadGlobalConfig() {
    const { getVariable } = this;
    const globalConfig = FEATURE_TO_VARIATION_CONFIG.global_config;
    return Object.keys(globalConfig).map((featureVariableKey) => {
      let value = getVariable('global_app_config', featureVariableKey, globalConfig[featureVariableKey]);
      if (globalConfig[featureVariableKey] === 'string') {
        try {
          const parsed = JSON.parse(value);
          value = parsed;
        } catch (err) {
          if (isDev()) {
            console.log('not a json object'); // eslint-disable-line no-console
          }
        }
      }
      return {
        [featureVariableKey]: value,
      };
    }).reduce((resultObj, keyValObject) => ({
      ...resultObj,
      ...keyValObject,
    }), {});
  }

  impression(feature, options) {
    const userId = this.getUserUUID();
    const impression = (options && typeof options.impression === 'boolean') ? options.impression : true;
    if (impression) {
      if (isDev()) {
        // eslint-disable-next-line
        console.log(`FIRING OPTIMIZELY ISFEATUREENABLED METHOD WITH FEATURE: ${feature} AND USER: ${userId}`);
      }
      this.isFeatureEnabled(feature, userId);
    }
  }

  eventHandler(event, attributes) {
    const experimentsConfig = FEATURE_TO_VARIATION_CONFIG.experiments;
    const experimentEvents = uniq(Object.keys(experimentsConfig)
      .map((featureName) => {
        const { events } = experimentsConfig[featureName];
        return events || [];
      })
      .reduce((resultArray, currentArray) => resultArray.concat(currentArray), []));
    if (experimentEvents.includes(event)) {
      const currentUserId = this.getUserUUID();
      if (currentUserId) {
        console.log(`OPTIMIZELY EVENT ${event} TRACKED WITH MPID ${currentUserId}.`); // eslint-disable-line no-console
        this.track(event, currentUserId, attributes);
      }
    }
  }

  getVariable(featureKey, featureVariable, varType) {
    const { getClient, getUserUUID } = this;
    const userId = getUserUUID();
    const varTypeCap = varType.charAt(0).toUpperCase() + varType.slice(1);
    return getClient()[`getFeatureVariable${varTypeCap}`](featureKey, featureVariable, userId) || null;
  }

  // all methods below map one-to-one to optimizely javascript sdk:
  // https://docs.developers.optimizely.com/full-stack/docs/activate
  activate(experimentKey, userUuid, attributes) {
    const { getClient } = this;
    return getClient().activate(experimentKey, userUuid, withDefaults(attributes));
  }

  getEnabledFeatures(userUuid, attributes) {
    const { getClient } = this;
    return getClient().getEnabledFeatures(userUuid, withDefaults(attributes));
  }

  getFeatureVariableBoolean(featureKey, variableKey, userUuid, attributes) {
    const { getClient } = this;
    return getClient().getFeatureVariableBoolean(featureKey, variableKey, userUuid, withDefaults(attributes));
  }

  getFeatureVariableDouble(featureKey, variableKey, userUuid, attributes) {
    const { getClient } = this;
    return getClient().getFeatureVariableDouble(featureKey, variableKey, userUuid, withDefaults(attributes));
  }

  getFeatureVariableInteger(featureKey, variableKey, userUuid, attributes) {
    const { getClient } = this;
    return getClient().getFeatureVariableInteger(featureKey, variableKey, userUuid, withDefaults(attributes));
  }

  getFeatureVariableString(featureKey, variableKey, userUuid, attributes) {
    const { getClient } = this;
    return getClient().getFeatureVariableString(featureKey, variableKey, userUuid, withDefaults(attributes));
  }

  getForcedVariation(experimentKey, userUuid) {
    const { getClient } = this;
    return getClient().getForcedVariation(experimentKey, userUuid);
  }

  getVariation(experimentKey, userUuid, attributes) {
    const { getClient } = this;
    return getClient().getVariation(experimentKey, userUuid, withDefaults(attributes));
  }

  isFeatureEnabled(featureKey, userUuid, attributes) {
    const { getClient } = this;
    return getClient().isFeatureEnabled(featureKey, userUuid, withDefaults(attributes));
  }

  setForcedVariation(experimentKey, userUuid, variationKey) {
    const { getClient } = this;
    return getClient().setForcedVariation(experimentKey, userUuid, variationKey);
  }

  track(eventKey, userUuid, attributes, tags) {
    const { getClient } = this;
    return getClient().track(eventKey, userUuid, withDefaults(attributes), safeDefault(tags));
  }
}

export default Optimizely;
